import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
} from "@mui/material";
import RateReviewIcon from "@mui/icons-material/RateReview";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";

const ManagerFeedbackForm = () => {
  const [employeeID, setEmployeeID] = useState("");
  const [workQuality, setWorkQuality] = useState("");
  const [productivity, setProductivity] = useState("");
  const [communicationSkills, setCommunicationSkills] = useState("");
  const [teamwork, setTeamwork] = useState("");
  const [problemSolvingSkills, setProblemSolvingSkills] = useState("");
  const [attendancePunctuality, setAttendancePunctuality] = useState("");
  const [goalsSet, setGoalsSet] = useState("");
  const [achievements, setAchievements] = useState("");
  const [skillsToImprove, setSkillsToImprove] = useState("");
  const [trainingNeeds, setTrainingNeeds] = useState("");
  const [managerFeedback, setManagerFeedback] = useState("");
  const [employeeComments, setEmployeeComments] = useState("");
  const [overallRating, setOverallRating] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [managerSignature, setManagerSignature] = useState("");
  const BASE_URL = process.env.REACT_APP_BASE_URL;


  const handleSubmit = async (e) => {
    e.preventDefault();
  
    const token = sessionStorage.getItem("token"); // Get the token from session storage
  
    try {
      const response = await fetch(`${BASE_URL}/api/feedback/managerfeedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`, // Include token in the request headers
          'Content-Type': 'application/json', // Specify content type as JSON
        },
        body: JSON.stringify({
          employeeID,
          workQuality,
          productivity,
          communicationSkills,
          teamwork,
          problemSolvingSkills,
          attendancePunctuality,
          goalsSet,
          achievements,
          skillsToImprove,
          trainingNeeds,
          managerFeedback,
          employeeComments,
          overallRating,
          recommendation,
          managerSignature, // Include this field
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json(); // Parse error response
        throw new Error(errorData.message || 'Failed to submit feedback');
      }
  
      const data = await response.json(); // Parse the response data
      console.log("Response:", data); // Log the response for debugging
      alert("Feedback submitted successfully!"); // Popup message for success
  
      // Clear the form fields after successful submission
      setEmployeeID("");
      setWorkQuality("");
      setProductivity("");
      setCommunicationSkills("");
      setTeamwork("");
      setProblemSolvingSkills("");
      setAttendancePunctuality("");
      setGoalsSet("");
      setAchievements("");
      setSkillsToImprove("");
      setTrainingNeeds("");
      setManagerFeedback("");
      setEmployeeComments("");
      setOverallRating("");
      setRecommendation("");
      setManagerSignature(""); // Clear managerSignature
  
    } catch (error) {
      console.error("Error submitting feedback:", error.message); // Log error message for debugging
      alert("Error submitting feedback. Please try again."); // Popup message for error
    }
  };
  
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      fontSize: "0.875rem",
      "& fieldset": { borderColor: "#eef0f6" },
      "&:hover fieldset": { borderColor: "#8b8fa3" },
      "&.Mui-focused fieldset": { borderColor: "#667eea", borderWidth: 1.5 },
    },
  };

  const sectionTitle = (title) => (
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: "0.95rem",
        color: "#1a1a2e",
        pb: 1.5,
        mb: 2,
        borderBottom: "1px solid #f0f2f8",
      }}
    >
      {title}
    </Typography>
  );

  const fieldLabel = (label) => (
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: "#5a5f7a", mb: 0.75 }}
    >
      {label}
    </Typography>
  );

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Performance Feedback"
        subtitle="Evaluate employee performance and provide actionable feedback"
        icon={<RateReviewIcon />}
      />

      <Paper
        elevation={0}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          border: "1px solid #eef0f6",
          borderRadius: "16px",
          p: 3,
        }}
      >
        {/* Employee Identification */}
        <Box sx={{ mb: 3 }}>
          {sectionTitle("Employee Identification")}
          <Box sx={{ maxWidth: 400 }}>
            {fieldLabel("Employee ID *")}
            <TextField
              fullWidth
              size="small"
              placeholder="Enter employee ID"
              value={employeeID}
              onChange={(e) => setEmployeeID(e.target.value)}
              required
              sx={inputSx}
            />
          </Box>
        </Box>

        {/* Performance Metrics */}
        <Box sx={{ mb: 3 }}>
          {sectionTitle("Performance Metrics")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              {fieldLabel("Work Quality *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Evaluate work quality"
                value={workQuality}
                onChange={(e) => setWorkQuality(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Productivity *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Evaluate productivity"
                value={productivity}
                onChange={(e) => setProductivity(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Communication Skills *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Evaluate communication"
                value={communicationSkills}
                onChange={(e) => setCommunicationSkills(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Teamwork *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Evaluate teamwork"
                value={teamwork}
                onChange={(e) => setTeamwork(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Problem Solving Skills *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Evaluate problem solving"
                value={problemSolvingSkills}
                onChange={(e) => setProblemSolvingSkills(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Attendance & Punctuality *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Evaluate attendance"
                value={attendancePunctuality}
                onChange={(e) => setAttendancePunctuality(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
          </Box>
        </Box>

        {/* Goals & Achievements */}
        <Box sx={{ mb: 3 }}>
          {sectionTitle("Goals & Achievements")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              {fieldLabel("Goals Set *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Describe goals set"
                value={goalsSet}
                onChange={(e) => setGoalsSet(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Achievements *")}
              <TextField
                fullWidth
                size="small"
                placeholder="List achievements"
                value={achievements}
                onChange={(e) => setAchievements(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
          </Box>
        </Box>

        {/* Development Areas */}
        <Box sx={{ mb: 3 }}>
          {sectionTitle("Development Areas")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              {fieldLabel("Skills to Improve *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Areas for improvement"
                value={skillsToImprove}
                onChange={(e) => setSkillsToImprove(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Training Needs *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Recommended training"
                value={trainingNeeds}
                onChange={(e) => setTrainingNeeds(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
          </Box>
        </Box>

        {/* Manager Feedback & Comments */}
        <Box sx={{ mb: 3 }}>
          {sectionTitle("Feedback & Comments")}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              {fieldLabel("Manager Feedback *")}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                placeholder="Provide detailed manager feedback..."
                value={managerFeedback}
                onChange={(e) => setManagerFeedback(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Employee Comments")}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={3}
                placeholder="Employee comments (optional)"
                value={employeeComments}
                onChange={(e) => setEmployeeComments(e.target.value)}
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Recommendation *")}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                placeholder="Provide recommendations..."
                value={recommendation}
                onChange={(e) => setRecommendation(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
          </Box>
        </Box>

        {/* Rating & Sign-off */}
        <Box sx={{ mb: 3 }}>
          {sectionTitle("Rating & Sign-off")}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              {fieldLabel("Overall Rating *")}
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Enter overall rating"
                value={overallRating}
                onChange={(e) => setOverallRating(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
            <Box>
              {fieldLabel("Manager Signature *")}
              <TextField
                fullWidth
                size="small"
                placeholder="Enter manager signature"
                value={managerSignature}
                onChange={(e) => setManagerSignature(e.target.value)}
                required
                sx={inputSx}
              />
            </Box>
          </Box>
        </Box>

        {/* Submit */}
        <Box sx={{ pt: 2, borderTop: "1px solid #eef0f6" }}>
          <Button
            type="submit"
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "0.875rem",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              px: 4,
              py: 1,
              boxShadow: "none",
              "&:hover": { background: "linear-gradient(135deg, #5a6fd8 0%, #6a3f9e 100%)", boxShadow: "none" },
            }}
          >
            Submit Feedback
          </Button>
        </Box>
      </Paper>
    </PageContainer>
  );
};

export default ManagerFeedbackForm;
